// A new dataset from one file (docs/ref-belonging-plan.md, step 4): every
// column becomes a term of the institution's own vocabulary, with a guessed
// kind the steward corrects, and the file becomes the dataset's first source.
// The quick way names things after the file (`ref:b1`); curating those names
// into shared terms is the vocabulary's work (step 5).
//
// Pure: the browser guesses and previews, the server builds and writes.

import { initialSourceConfig } from './source_setup.js';
import { reusedEntries, reusedTransform } from './vocabulary_match.js';
import { NEW_DATASET_RULES } from './datasets.js';

export const KINDS = ['scale', 'category', 'list', 'number', 'text', 'date', 'key', 'skip'];
// Kinds that become a term. A date or key column is housekeeping (the period,
// the watermark, the record's key), never a term.
export const TERM_KINDS = new Set(['scale', 'category', 'list', 'number', 'text']);
// Kinds whose values are a list the steward confirms before it is published.
export const LISTED_KINDS = new Set(['category', 'list']);

const MAX_CATEGORY_VALUES = 12;
const MAX_LIST_VALUES = 24;
const MAX_CATEGORY_LENGTH = 30;
const DELIMITERS = ['|', ';'];
const DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?/;
const INTEGER = /^-?\d+$/;
const NUMBER = /^-?\d+(?:\.\d+)?$/;

// Shared: may be counted in a cube on its own. Never: listed in the
// vocabulary so the dataset's terms are complete, but never mapped or shared.
const SHARED = { identifies: 'no', sensitivity: 'personal', treatment: 'isolate' };
const NEVER = { identifies: 'no', sensitivity: 'personal', treatment: 'drop' };

const count = (values) => {
  const counts = {};
  for (const value of values) counts[value] = (counts[value] ?? 0) + 1;
  return counts;
};
const sortedKeys = (counts) => Object.keys(counts).sort(compareValues);

// A scale is confirmed as the form's range, not the values seen: 2–5 seen is
// most likely a 1–5 scale nobody answered 1 on.
function guessRange(numbers) {
  const min = Math.min(...numbers), max = Math.max(...numbers);
  return { low: min >= 1 ? 1 : min, high: [5, 7, 10].find((top) => max <= top) ?? max };
}

// Each cell split on the delimiter, counted per part: a row holding two
// genres counts once for each.
function splitCounts(raw, delimiter) {
  const counts = {};
  for (const [value, n] of Object.entries(raw)) {
    for (const part of value.split(delimiter).map((p) => p.trim()).filter(Boolean)) counts[part] = (counts[part] ?? 0) + n;
  }
  return counts;
}

// A list when enough cells hold several short values: `Alternative|Fado`.
function guessDelimiter(raw) {
  const cells = Object.entries(raw), filled = cells.reduce((sum, [, n]) => sum + n, 0);
  return DELIMITERS.find((delimiter) => {
    const joined = cells.filter(([value]) => value.includes(delimiter));
    const several = joined.reduce((sum, [, n]) => sum + n, 0);
    const parts = Object.keys(splitCounts(raw, delimiter));
    return (joined.length >= 2 || several >= filled / 10) && parts.length <= MAX_LIST_VALUES && parts.every((p) => p.length <= MAX_CATEGORY_LENGTH);
  }) ?? null;
}

// Band edges at about a third and two thirds of the rows, rounded to
// something a person would pick (60, not 58.5).
const nice = (x) => {
  if (x >= 100) { const unit = 10 ** (Math.floor(Math.log10(x)) - 1); return Math.round(x / unit) * unit; }
  return x >= 10 ? Math.round(x / 5) * 5 : Math.round(x);
};
function guessEdges(raw) {
  const numbers = Object.entries(raw).flatMap(([value, n]) => Array(n).fill(Number(value))).sort((a, b) => a - b);
  const at = (p) => numbers[Math.floor(p * (numbers.length - 1))];
  const edges = [...new Set([nice(at(1 / 3)), nice(at(2 / 3))])].filter((edge) => edge < numbers.at(-1));
  return edges.length ? edges : [nice(at(1 / 2))];
}

// Edges are inclusive upper bounds (the `bucket` transform): 60, 90 gives
// "up to 60", "61–90", "over 90".
export function bandLabels(edges, integers = true) {
  return [`up to ${edges[0]}`,
    ...edges.slice(1).map((edge, i) => `${integers ? edges[i] + 1 : edges[i]}–${edge}`),
    `over ${edges.at(-1)}`];
}
export const integersOnly = (column) => Object.keys(column.raw).every((value) => INTEGER.test(value));
export const edgesProblem = (edges) => !Array.isArray(edges) || !edges.length || edges.some((edge) => !Number.isFinite(edge))
  ? 'give the band edges as numbers, for example 60, 90.'
  : edges.some((edge, i) => i && edge <= edges[i - 1]) ? 'the band edges must go up.' : null;
// How many rows fall in each band.
export function bands(column) {
  if (edgesProblem(column.edges)) return [];
  const labels = bandLabels(column.edges, integersOnly(column));
  const counts = labels.map(() => 0);
  for (const [value, n] of Object.entries(column.raw)) {
    const i = column.edges.findIndex((edge) => Number(value) <= edge);
    counts[i === -1 ? labels.length - 1 : i] += n;
  }
  return labels.map((label, i) => ({ label, count: counts[i] }));
}

// The column as a value type: its values as that type reads them, and the
// type's own settings, guessed where it has none yet. The steward's choice of
// type goes through here too, so switching back and forth is lossless.
export function withKind(column, kind) {
  const next = { ...column, kind, shared: ['scale', 'category', 'list', 'number'].includes(kind), checked: false };
  next.counts = kind === 'list' ? splitCounts(column.raw, column.delimiter ?? '|') : column.raw;
  next.values = sortedKeys(next.counts);
  if (kind === 'list') next.delimiter = column.delimiter ?? '|';
  if (kind === 'scale' && !column.range) {
    const numbers = Object.keys(column.raw).filter((value) => INTEGER.test(value)).map(Number);
    next.range = numbers.length ? guessRange(numbers) : { low: 1, high: 5 };
  }
  if (kind === 'number') {
    next.shareAs = column.shareAs ?? 'bands';
    if (!column.edges && Object.keys(column.raw).every((value) => NUMBER.test(value))) next.edges = guessEdges(column.raw);
  }
  return next;
}

// One row per column: its value type, meaning and sharing, guessed from the
// values in the file. `parsed` is parseAndProfileCsv's result.
export function guessColumns(parsed) {
  const rows = parsed.rows.length;
  let keyTaken = false;
  return parsed.headers.map((name) => {
    const values = parsed.rows.map((row) => String(row[name] ?? '').trim()).filter(Boolean);
    const raw = count(values);
    const distinct = Object.keys(raw);
    const column = { name, text: name, filled: values.length, raw };
    if (!values.length || distinct.length === 1) return { ...withKind(column, 'skip'), why: values.length ? 'the same in every row' : 'empty' };
    if (values.every((value) => DATE.test(value) && !Number.isNaN(Date.parse(value)))) return withKind(column, 'date');
    if (!keyTaken && values.length === rows && distinct.length === rows) { keyTaken = true; return withKind(column, 'key'); }
    if (distinct.every((value) => NUMBER.test(value))) {
      const numbers = distinct.map(Number);
      const small = numbers.every(Number.isInteger) && Math.min(...numbers) >= 0 && Math.max(...numbers) <= 10;
      return withKind(column, small ? 'scale' : 'number');
    }
    const delimiter = guessDelimiter(raw);
    if (delimiter) return withKind({ ...column, delimiter }, 'list');
    if (distinct.length <= MAX_CATEGORY_VALUES && distinct.every((value) => value.length <= MAX_CATEGORY_LENGTH)) {
      return withKind(column, 'category');
    }
    return withKind(column, 'text');
  });
}

function compareValues(a, b) {
  return NUMBER.test(a) && NUMBER.test(b) ? Number(a) - Number(b) : a.localeCompare(b);
}

// The local part of a term's id: the header itself when it is one already.
export function localName(header) {
  const clean = String(header).trim().replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '');
  return /^[A-Za-z_]/.test(clean) ? clean : `q_${clean || 'column'}`;
}
const pascal = (text) => String(text).trim().split(/[^A-Za-z0-9]+/).filter(Boolean)
  .map((word) => word[0].toUpperCase() + word.slice(1)).join('') || 'Record';
const slug = (text) => String(text).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'value';

export const datasetIdFrom = (label) => slug(label).replace(/^[^a-z]+/, '') || 'dataset';
export const recordClassFrom = (prefix, singular) => `${prefix}:${pascal(singular)}`;
export const unitFrom = (singular) => {
  const word = String(singular).trim() || 'record';
  return word.endsWith('s') ? word : `${word}s`;
};

const scaleSchemeId = (prefix, { low, high }) => `${prefix}:Scale${low}to${high}`;

function scaleScheme(prefix, range, source) {
  const values = Array.from({ length: range.high - range.low + 1 }, (_, i) => String(range.low + i));
  const half = Math.floor(values.length / 2);
  return {
    id: scaleSchemeId(prefix, range), from: prefix, label: `${range.low}–${range.high} scale`,
    output: 'typed', datatype: 'xsd:integer', means: `A scale from ${range.low} to ${range.high}.`,
    scale: { low: values.slice(0, half), middle: values.length % 2 ? [values[half]] : [], high: values.slice(-half) },
    concepts: values.map((id) => ({ id, label: id })), derivedFrom: { source: source.id },
  };
}

function categoryScheme(prefix, local, column, source, labels = column.values, suffix = 'Values') {
  const used = new Set();
  const concepts = labels.map((value) => {
    let id = `${prefix}:${local}-${slug(value)}`;
    while (used.has(id)) id += '-x';
    used.add(id);
    return { id, label: value, aliases: [value] };
  });
  return {
    id: `${prefix}:${local}${suffix}`, from: prefix, output: 'iri',
    means: `The values of “${column.text}”, as listed when the dataset was made from ${source.fileName}.`,
    members: concepts.map((concept) => concept.id), concepts, derivedFrom: { source: source.id },
  };
}

// The period every record of the file falls in: a quarter or year of its
// timestamp, one round label for the whole file (a survey's T0), or a column.
export function periodColumn(period, timestampColumn) {
  const shared = { record: 'shared', target: 'core:period' };
  if (period?.kind === 'fixed') return { ...shared, source: '@period', transform: { fn: 'const', value: String(period.value ?? '').trim() } };
  if (period?.kind === 'column') return { ...shared, source: period.column, transform: { fn: 'direct' } };
  if (!timestampColumn) return null;
  return { ...shared, source: timestampColumn, transform: { fn: 'dateBucket', granularity: period?.kind === 'year' ? 'year' : 'quarter' } };
}

export function periodProblem(period, { timestampColumn, headers }) {
  if (period?.kind === 'fixed' && !String(period.value ?? '').trim()) return 'Name the round, for example T0.';
  if (period?.kind === 'column' && !headers.includes(period.column)) return 'Choose the column that holds the round.';
  if (['quarter', 'year'].includes(period?.kind ?? 'quarter') && !timestampColumn) return 'Choose a timestamp column, or one round for the whole file.';
  return null;
}

// The steward's review applied to the file itself: the values and counts
// come from the file, only the choices from the review. Added values (an
// option nobody picked yet) are kept, in the order given.
export function reviewedColumns(parsed, submitted) {
  if (!Array.isArray(submitted) || submitted.length !== parsed.headers.length
    || submitted.some((column, i) => column?.name !== parsed.headers[i])) {
    throw Object.assign(new Error('The reviewed columns do not match the file. Choose it again.'), { status: 400 });
  }
  const guessed = guessColumns(parsed);
  return submitted.map((choice, i) => {
    if (!KINDS.includes(choice.kind)) throw Object.assign(new Error(`${choice.name}: unknown value type.`), { status: 400 });
    const pick = ['text', 'shared', 'checked', 'range', 'edges', 'shareAs', 'use'].filter((key) => choice[key] !== undefined);
    const base = { ...guessed[i], ...(choice.delimiter && DELIMITERS.concat(',').includes(choice.delimiter) ? { delimiter: choice.delimiter } : {}) };
    const column = { ...withKind(base, choice.kind), ...Object.fromEntries(pick.map((key) => [key, choice[key]])) };
    if (LISTED_KINDS.has(column.kind) && Array.isArray(choice.values)) {
      column.values = [...column.values, ...choice.values.map(String).filter((v) => v.trim() && !column.values.includes(v))];
    }
    return column;
  });
}

// What the steward must still do before Confirm; empty when it can be saved.
export function columnProblems(columns) {
  const problems = [];
  for (const column of columns) {
    // A column read as a reused term takes that term's values and sharing.
    if (column.use) continue;
    if (column.kind === 'scale') {
      const { low, high } = column.range ?? {};
      if (!Number.isInteger(low) || !Number.isInteger(high) || high <= low) problems.push(`${column.name}: give the scale a range, low to high.`);
      else if (column.values.some((value) => !INTEGER.test(value) || Number(value) < low || Number(value) > high)) {
        problems.push(`${column.name}: the file holds values outside ${low}–${high}.`);
      }
    }
    if (column.kind === 'number') {
      if (Object.keys(column.raw).some((value) => !NUMBER.test(value))) problems.push(`${column.name}: not every value is a number.`);
      else if (column.shareAs === 'bands' && edgesProblem(column.edges)) problems.push(`${column.name}: ${edgesProblem(column.edges)}`);
    }
    if (LISTED_KINDS.has(column.kind) && column.shared && !column.checked) problems.push(`${column.name}: check its list of values.`);
    if (TERM_KINDS.has(column.kind) && !String(column.text ?? '').trim()) problems.push(`${column.name}: say what it means.`);
  }
  return problems;
}

// The vocabulary, dataset entry, connector and mapping a confirmed review
// makes. `source` is validateSourceDraft's result (id, label, fileName,
// sourceKey, timestampColumn, parsed); `columns` the reviewed guessColumns rows.
//
// Terms are named after the columns (`ref:b1`) while those names are free in
// `catalog`. When any of them is taken, by another dataset made from a copy of
// the same file, every name of this dataset is qualified by its id instead
// (`ref:belonging_test_b1`, `ref:BelongingTestResponse`). A value list the
// same as one already there (the 1–5 scale) is shared, not a clash.
//
// With `vocabulary` (lib/vocabulary_match.js vocabularyOf), a column whose
// `use` names one of its terms is read as that term, and the records are of
// that term's record class; what of the vocabulary we do not hold yet is
// added to ours as reused. The other columns become our own terms as above.
export function buildDataset({ dataset, catalog = null, vocabulary = null, ...rest }) {
  const reuse = reusing(vocabulary, rest.columns);
  const plain = buildNamed({ dataset, reuse, ...rest }, null);
  const built = vocabularyClashes(catalog, plain.vocabulary).length
    ? buildNamed({ dataset, reuse, ...rest }, qualifierOf(dataset.id)) : plain;
  if (!reuse) return built;
  const reused = reusedEntries(catalog, vocabulary, reuse.terms.map((t) => t.id));
  return { ...built, reused };
}

// The reused terms by column, and the one record class they describe.
function reusing(vocabulary, columns) {
  const used = columns.filter((column) => column.use);
  if (!used.length) return null;
  if (!vocabulary) throw Object.assign(new Error('A column names a term, but no vocabulary was chosen.'), { status: 400 });
  const byId = new Map(vocabulary.terms.map((t) => [t.id, t]));
  const unknown = used.filter((column) => !byId.has(column.use));
  if (unknown.length) {
    throw Object.assign(new Error(`${unknown.map((c) => `${c.name}: ${c.use}`).join(', ')} is not a term of ${vocabulary.prefix}.`), { status: 400 });
  }
  const twice = used.find((column, i) => used.findIndex((other) => other.use === column.use) !== i);
  if (twice) throw Object.assign(new Error(`${twice.use} is used for more than one column.`), { status: 400 });
  const terms = used.map((column) => byId.get(column.use));
  const classes = [...new Set(terms.map((t) => [t.record].flat()[0]))];
  if (classes.length !== 1) {
    throw Object.assign(new Error(`The chosen terms describe different records (${classes.join(', ')}); a dataset made from one file holds one.`), { status: 400 });
  }
  return { recordClass: classes[0], terms, byColumn: new Map(used.map((column) => [column.name, byId.get(column.use)])), vocabulary };
}

// Another source for a dataset (step 7.4): its file described with the
// dataset's own terms (`vocabulary`, datasetVocabulary's for `recordClass`).
// A column whose `use` names one of them is read as that term, keeping its
// sharing; any other column that is not left out becomes a new term of ours,
// of the same record class, named as a new dataset's would be. Its names are
// qualified by the dataset's id, then by the source's too, while taken.
export function buildSource({ dataset, recordClass, catalog, vocabulary, ...rest }) {
  const byId = new Map(vocabulary.terms.map((t) => [t.id, t]));
  const used = rest.columns.filter((column) => column.use);
  const unknown = used.filter((column) => !byId.has(column.use));
  if (unknown.length) {
    throw Object.assign(new Error(`${unknown.map((c) => `${c.name}: ${c.use}`).join(', ')} is not a term of ${dataset.label}.`), { status: 400 });
  }
  const twice = used.find((column, i) => used.findIndex((other) => other.use === column.use) !== i);
  if (twice) throw Object.assign(new Error(`${twice.use} is used for more than one column.`), { status: 400 });
  const reuse = { recordClass, vocabulary, byColumn: new Map(used.map((column) => [column.name, byId.get(column.use)])) };
  const named = (qualifier) => buildNamed({ dataset, reuse, ...rest }, qualifier);
  const tries = [null, qualifierOf(dataset.id), qualifierOf(`${dataset.id}_${rest.source.id}`)];
  const built = tries.map(named).find((b) => !vocabularyClashes(catalog, b.vocabulary).length) ?? named(tries.at(-1));
  return { vocabulary: built.vocabulary, connector: built.connector, mapping: built.mapping };
}

// A scheme counts as the same when everything but where it came from is.
const withoutOrigin = ({ derivedFrom, ...entry }) => JSON.stringify(entry);
function vocabularyClashes(catalog, vocabulary) {
  const problems = [];
  for (const [prefix, namespace] of Object.entries(vocabulary.prefixes)) {
    const existing = catalog?.prefixes?.[prefix];
    if (existing && existing !== namespace) problems.push(`The prefix ${prefix}: already stands for ${existing}.`);
  }
  for (const key of ['recordClasses', 'terms', 'valueSchemes']) {
    const held = new Map((catalog?.[key] ?? []).map((entry) => [entry.id, entry]));
    for (const entry of vocabulary[key]) {
      const existing = held.get(entry.id);
      if (existing && !(key === 'valueSchemes' && withoutOrigin(existing) === withoutOrigin(entry))) {
        problems.push(`${entry.id} is already in the vocabulary.`);
      }
    }
  }
  return problems;
}

// The names a dataset's record class and terms get: plain, or qualified by
// the dataset's id when the plain ones are taken. The new-dataset screen
// shows the same names before the server decides.
export const qualifierOf = (datasetId) => String(datasetId).replace(/[^A-Za-z0-9]+/g, '_');
export function namesFor(prefix, singular, qualifier = null) {
  return {
    recordClass: qualifier ? `${prefix}:${pascal(qualifier)}${pascal(singular)}` : recordClassFrom(prefix, singular),
    term: (header) => `${prefix}:${qualifier ? `${qualifier}_` : ''}${localName(header)}`,
  };
}

function buildNamed({ dataset, source, columns, period, prefix, namespace, institution, podBase, publisher, reuse }, qualifier) {
  const names = namesFor(prefix, dataset.singular, qualifier);
  const recordClass = reuse?.recordClass ?? names.recordClass;
  const derivedFrom = (column) => ({ source: source.id, column: column.name });
  const terms = [], schemes = new Map(), mapped = [];
  for (const column of columns) {
    const term = reuse?.byColumn.get(column.name);
    if (term) {
      const values = column.delimiter && term.unit === 'list' ? Object.keys(splitCounts(column.raw, column.delimiter)) : Object.keys(column.raw);
      const transform = reusedTransform(term, reuse.vocabulary, values, term.unit === 'list' ? column.delimiter : null);
      if (transform) {
        const { identifies, sensitivity, treatment } = term.posture ?? SHARED;
        mapped.push({ record: recordClass, target: term.id, source: column.name, term: term.id, identifies, sensitivity, treatment, transform });
      }
      continue;
    }
    if (!TERM_KINDS.has(column.kind)) continue;
    const id = names.term(column.name);
    const local = id.slice(prefix.length + 1);
    const shared = column.kind === 'number' ? column.shareAs !== 'never' : column.kind !== 'text' && column.shared;
    // A value list is published with the vocabulary, so only a shared
    // column's, and only once the steward has checked it. A number shared as
    // a total has none: a cube adds it up.
    let scheme = null, transform = null;
    if (shared && column.kind === 'scale') {
      scheme = scaleScheme(prefix, column.range, source);
      transform = { fn: 'mapValues', output: 'typed', datatype: 'xsd:integer', default: 'drop',
        map: Object.fromEntries(scheme.concepts.map((concept) => [concept.id, concept.id])) };
    }
    if (shared && LISTED_KINDS.has(column.kind)) {
      scheme = categoryScheme(prefix, local, column, source);
      const toConcept = { fn: 'mapValues', output: 'iri', default: 'drop',
        map: Object.fromEntries(scheme.concepts.map((concept) => [concept.label, concept.id])) };
      transform = column.kind === 'list' ? [{ fn: 'split', delimiter: column.delimiter }, toConcept] : toConcept;
    }
    if (shared && column.kind === 'number' && column.shareAs === 'bands') {
      const labels = bandLabels(column.edges, integersOnly(column));
      scheme = categoryScheme(prefix, local, column, source, labels, 'Bands');
      transform = [{ fn: 'bucket', parse: 'number', edges: column.edges, labels },
        { fn: 'mapValues', output: 'iri', default: 'drop',
          map: Object.fromEntries(scheme.concepts.map((concept) => [concept.label, concept.id])) }];
    }
    if (shared && column.kind === 'number' && column.shareAs === 'total') {
      transform = { fn: 'direct', output: 'typed', datatype: integersOnly(column) ? 'xsd:integer' : 'xsd:decimal' };
    }
    if (scheme && !schemes.has(scheme.id)) schemes.set(scheme.id, scheme);
    terms.push({ id, from: prefix, notation: column.name, record: recordClass, means: String(column.text).trim(),
      posture: shared ? SHARED : NEVER, unit: column.kind, ...(scheme ? { valueScheme: scheme.id } : {}),
      ...(column.kind === 'number' && shared ? { sharedAs: column.shareAs } : {}),
      derivedFrom: derivedFrom(column), note: `Column ${column.name} of ${source.fileName}.` });
    if (!shared) continue;
    mapped.push({ record: recordClass, target: id, source: column.name, term: id, ...SHARED, transform });
  }
  const datasetEntry = { id: dataset.id, label: dataset.label.trim(), recordClasses: [recordClass], ...NEW_DATASET_RULES };
  const vocabulary = {
    prefixes: terms.length || !reuse ? { [prefix]: namespace } : {},
    recordClasses: reuse ? [] : [{ id: recordClass, from: prefix, unit: unitFrom(dataset.singular),
      means: `One row of ${source.fileName}, the file the ${datasetEntry.label} dataset was made from.`,
      derivedFrom: { source: source.id } }],
    terms, valueSchemes: [...schemes.values()],
  };
  const records = { [recordClass]: { raw: `sources/${source.id}/` } };
  const { connector, mapping } = initialSourceConfig(source, { institution, podBase, publisher, records,
    period: periodColumn(period, source.timestampColumn) });
  mapping.columns.push(...mapped);
  const read = new Set(mapping.columns.map((column) => column.source));
  mapping.drop = source.parsed.headers.filter((header) => !read.has(header));
  return { dataset: datasetEntry, vocabulary, connector, mapping };
}

// The catalog with a derived vocabulary added. Nothing it holds is replaced:
// an id or prefix already there under another meaning is refused, and a value
// list already there as it is is shared.
export function addToCatalog(catalog, vocabulary, reused = null) {
  const problems = vocabularyClashes(catalog, vocabulary);
  for (const [prefix, namespace] of Object.entries(reused?.prefixes ?? {})) {
    const existing = catalog?.prefixes?.[prefix];
    if (existing && existing !== namespace) problems.push(`The prefix ${prefix}: already stands for ${existing}.`);
  }
  if (problems.length) throw Object.assign(new Error(problems.join(' ')), { status: 409 });
  const held = new Set((catalog.valueSchemes ?? []).map((scheme) => scheme.id));
  const more = (key) => reused?.[key] ?? [];
  return {
    ...catalog,
    prefixes: { ...catalog.prefixes, ...more('prefixes'), ...vocabulary.prefixes },
    ...(more('sections').length ? { sections: [...(catalog.sections ?? []), ...more('sections')] } : {}),
    recordClasses: [...(catalog.recordClasses ?? []), ...more('recordClasses'), ...vocabulary.recordClasses],
    terms: [...(catalog.terms ?? []), ...more('terms'), ...vocabulary.terms],
    valueSchemes: [...(catalog.valueSchemes ?? []), ...more('valueSchemes'),
      ...vocabulary.valueSchemes.filter((scheme) => !held.has(scheme.id))],
  };
}
